/**
 * The planner's brief, shown as a collapsed strip above the answer.
 *
 * Mostly it tells you what the answer is *committed* to covering — which is
 * useful before you've read it, and useful afterwards as a checklist.
 */

import { useState } from "react";
import type { LessonPlan } from "../../shared/agents.ts";
import { DEPTH_BY_ID } from "../../shared/depth.ts";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "./ui/collapsible.tsx";
import { Badge } from "./ui/badge.tsx";
import { MathText } from "../markdown/MathText.tsx";
import { Separator } from "./ui/separator.tsx";

export function PlanCard({ plan }: { plan: LessonPlan }) {
  const [open, setOpen] = useState(false);

  const hasDetail =
    plan.keyIdeas.length > 0 ||
    plan.sanityChecks.length > 0 ||
    plan.commonPitfalls.length > 0 ||
    plan.prerequisites.length > 0;

  if (plan.answerShape === "chat") return null;

  return (
    <Collapsible
      open={open}
      onOpenChange={setOpen}
      className="mb-4 rounded-md border bg-muted font-sans"
    >
      <h3 className="sr-only">Lesson brief</h3>

      <CollapsibleTrigger
        disabled={!hasDetail}
        className="flex w-full items-center gap-3 px-3 py-2 text-left text-sm disabled:cursor-default"
      >
        <span className="min-w-0 flex-1 truncate text-secondary-foreground">
          <MathText children={plan.topic} />
        </span>
        <Badge variant="outline" className="bg-card font-normal">
          {plan.level}
        </Badge>
        {/* The length this answer was actually written to — the server rewrites
            the planner's suggestion to the resolved value, so this is honest
            whether the reader chose Auto or picked a length themselves. */}
        <Badge variant="outline" className="bg-card font-normal">
          {DEPTH_BY_ID[plan.suggestedDepth].label}
        </Badge>
        {hasDetail && (
          <span className="text-xs text-muted-foreground">
            {open ? "hide brief" : "brief"}
          </span>
        )}
      </CollapsibleTrigger>

      <CollapsibleContent>
        <Separator />
        <div className="grid gap-4 p-3 sm:grid-cols-2">
          <PlanList title="Should land" items={plan.keyIdeas} />
          <PlanList title="Must check" items={plan.sanityChecks} />
          <PlanList title="Watch out for" items={plan.commonPitfalls} />
          <PlanList title="Assumes you know" items={plan.prerequisites} />
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

interface PlanListProps {
  readonly title: string;
  readonly items: readonly string[];
}

function PlanList({ title, items }: PlanListProps) {
  if (items.length === 0) return null;

  return (
    <div className="text-sm">
      <h4 className="mb-1 text-xs font-semibold tracking-wide text-muted-foreground uppercase">
        {title}
      </h4>
      <ul className="list-disc space-y-1 pl-4 text-secondary-foreground marker:text-muted-foreground">
        {items.map((item, index) => (
          <li key={index}>
            <MathText children={item} />
          </li>
        ))}
      </ul>
    </div>
  );
}
